import React from 'react';
import Markdown from 'react-markdown';
import { Body5 } from '@ui';
import { answerFormatter } from '@classes/AnswerFormatter';
import { resolveMarkdownImage } from '../utils/markdownImages';
import { MarkdownAnswerStyles } from '../styles/MarkdownAnswerStyles';
import { useChunkedMarkdown } from '@hooks/features/useChunkedMarkdown';
import { Citation } from './Citations';
import { MoreDetail } from './MoreDetail';

const isWhitespaceChild = (child) => typeof child === 'string' && !child.trim();

const MarkdownAnswerImage = ({ className, ...props }) => {
  const resolvedClassName = ['markdown-answer-image', className].filter(Boolean).join(' ');
  return <img {...props} className={resolvedClassName} />;
};

const isMarkdownAnswerImageChild = (child) => React.isValidElement(child) && child.type === MarkdownAnswerImage;

const FrozenChunk = React.memo(({ markdown, components }) => {
  const plugins = React.useMemo(() => answerFormatter.getPluginsForResult(markdown), [markdown]);
  return (
    <Markdown rehypePlugins={plugins} components={components}>
      {markdown}
    </Markdown>
  );
});

export const AnswerBody = ({
  answer,
  isStreaming = false,
  enableLinks = false,
  enableMoreDetail = true,
  imageSourceLookup = null,
  ignoreLatex = false,
}) => {
  const { frozenChunks, activeTail } = useChunkedMarkdown(answer, isStreaming);

  const markdownComponents = React.useMemo(
    () => ({
      li(props) {
        const { node, ...rest } = props;
        if (enableMoreDetail) {
          return <MoreDetail {...rest} isBullet />;
        }
        return <li {...rest} className={'no-detail'} />;
      },
      a(props) {
        const { node, ...rest } = props;
        if (answerFormatter.isCitationLink(rest.href)) {
          return (
            <Citation
              data={rest.children}
              url={rest.href}
              // metadata={rest}
            />
          );
        }
        if (enableLinks) {
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          return <a {...rest} target="_blank" rel="noopener noreferrer" />;
        }
        return null;
      },
      img(props) {
        const { node, ...rest } = props;
        const matchedImage = resolveMarkdownImage(rest.src, imageSourceLookup);
        const resolvedSrc = matchedImage?.src || rest.src;

        if (!resolvedSrc) return null;

        return <MarkdownAnswerImage {...rest} src={resolvedSrc} alt={rest.alt || matchedImage?.altText || ''} />;
      },
      p(props) {
        const { node, ...rest } = props;
        const childArray = React.Children.toArray(rest.children);
        const imageChildren = childArray.filter(isMarkdownAnswerImageChild);
        const isImageOnlyParagraph =
          imageChildren.length > 0 &&
          childArray.every((child) => isMarkdownAnswerImageChild(child) || isWhitespaceChild(child));

        if (isImageOnlyParagraph) {
          if (imageChildren.length === 1) {
            return <div className="markdown-answer-image-single">{imageChildren[0]}</div>;
          }

          return <div className="markdown-answer-image-grid">{imageChildren}</div>;
        }

        return (
          <p>
            <Body5>{rest.children}</Body5>
          </p>
        );
      },
    }),
    [enableLinks, enableMoreDetail, imageSourceLookup]
  );

  const activePlugins = React.useMemo(() => answerFormatter.getPluginsForResult(activeTail), [activeTail]);

  if (!answer) return null;
  return (
    <MarkdownAnswerStyles>
      {frozenChunks.map((chunk) => (
        <FrozenChunk key={chunk.id} markdown={chunk.markdown} components={markdownComponents} />
      ))}
      {activeTail && (
        <Markdown rehypePlugins={activePlugins} components={markdownComponents}>
          {activeTail}
        </Markdown>
      )}
    </MarkdownAnswerStyles>
  );
};
